import { Input, InputNumber } from "antd"
import { placementsEnum } from "../types/types"
import { COLUMNS_SIZE_MAP } from "../constants/table.constants"
import { IPlacementDataTable } from "../types/placement"
import SelectAudience from "../components/SelectAudience"
import FvVideoPreview from "../components/FvVideoPreview"
import { IPlacementDataForUpdate } from "../store/types/placements"

interface IUsePlacementColumnsProps {
  handleLimitedCPCChange: (placement: IPlacementDataTable, value: number) => void
  handleTargetAcosChange: (placement: IPlacementDataTable, value: number) => void
  setPlacementsDataForUpdate: React.Dispatch<React.SetStateAction<IPlacementDataForUpdate[]>>
}

const FV_PLACEHOLDER = "-"

const isFvRow = (placement: IPlacementDataTable | undefined): boolean =>
  placement?.placement === placementsEnum.FEATURED_VIDEOS

const usePlacementColumns = (params: IUsePlacementColumnsProps) => {
  const { handleLimitedCPCChange, handleTargetAcosChange, setPlacementsDataForUpdate } = params

  return [
    {
      width: COLUMNS_SIZE_MAP["1"] + 80,
    },
    {
      dataIndex: "targetAcos",
      render: (targetAcos: number, placement: IPlacementDataTable) => (
        <>
          <Input
            value={isFvRow(placement) ? FV_PLACEHOLDER : targetAcos}
            type="text"
            className="cal"
            disabled={isFvRow(placement)}
            onChange={(e) => handleTargetAcosChange(placement, Number(e.target.value))}
          />
        </>
      ),
      width: COLUMNS_SIZE_MAP["2"],
    },
    {
      render: (row: IPlacementDataTable) => {
        if (row.placement === placementsEnum.AUDIENCE) {
          return (
            <SelectAudience
              audienceId={row.audienceId}
              campaignId={row.campaignId}
              setPlacementsDataForUpdate={setPlacementsDataForUpdate}
            />
          )
        }
        if (row.placement === placementsEnum.FEATURED_VIDEOS) {
          return <FvVideoPreview placement={row} />
        }
        return row.placement
      },
      width: COLUMNS_SIZE_MAP["3"],
    },
    {
      width: COLUMNS_SIZE_MAP["4"],
    },
    {
      width: COLUMNS_SIZE_MAP["5"],
    },
    {
      dataIndex: "placementModifier",
      render: (e, placement: IPlacementDataTable) =>
        isFvRow(placement) ? FV_PLACEHOLDER : `${e ? e : 0}%`,
      width: COLUMNS_SIZE_MAP["6"],
    },
    {
      dataIndex: "targetCPCAmazon",
      render: (e, placement: IPlacementDataTable) =>
        isFvRow(placement) ? FV_PLACEHOLDER : `${e || 0}$`,
      width: COLUMNS_SIZE_MAP["7"],
    },
    {
      dataIndex: "targetCPC",
      render: (targetCPC, placement: IPlacementDataTable) =>
        isFvRow(placement) ? FV_PLACEHOLDER : `${targetCPC?.toFixed(2) || 0}$`,
      width: COLUMNS_SIZE_MAP["8"],
    },
    {
      dataIndex: "bestCPC",
      render: (bestCPC, placement: IPlacementDataTable) =>
        isFvRow(placement) ? FV_PLACEHOLDER : `${bestCPC || 0}$`,
      width: COLUMNS_SIZE_MAP["9"],
    },
    {
      dataIndex: "limitedCPC",
      render: (limitedCPC: number, placement: IPlacementDataTable) => {
        if (isFvRow(placement)) {
          return <InputNumber value={FV_PLACEHOLDER} disabled />
        }
        return (
          <InputNumber
            value={limitedCPC?.toFixed(2) || 0}
            step="0.01"
            onChange={(value) => handleLimitedCPCChange(placement, Number(value))}
            stringMode
          />
        )
      },
      width: COLUMNS_SIZE_MAP["10"],
    },
    {
      title: "New Bid Adjustment",
      dataIndex: "newBidAdjustment",
      render: (newBidAdjustment: number, placement: IPlacementDataTable) =>
        isFvRow(placement) ? FV_PLACEHOLDER : (newBidAdjustment ?? 0).toFixed(0) + "%",
      width: COLUMNS_SIZE_MAP["11"],
    },
    {
      dataIndex: "impressions",
      render: (e, placement: IPlacementDataTable) => (isFvRow(placement) ? FV_PLACEHOLDER : e),
      width: COLUMNS_SIZE_MAP["12"],
    },
    {
      dataIndex: "clicks",
      render: (e, placement: IPlacementDataTable) => (isFvRow(placement) ? FV_PLACEHOLDER : e),
      width: COLUMNS_SIZE_MAP["13"],
    },
    {
      dataIndex: "ctr",
      render: (e, placement: IPlacementDataTable) =>
        isFvRow(placement) ? FV_PLACEHOLDER : `${e}%`,
      width: COLUMNS_SIZE_MAP["14"],
    },
    {
      dataIndex: "spend",
      render: (e, placement: IPlacementDataTable) =>
        isFvRow(placement) ? FV_PLACEHOLDER : `${e}$`,
      width: COLUMNS_SIZE_MAP["15"],
    },
    {
      dataIndex: "sales",
      render: (e, placement: IPlacementDataTable) =>
        isFvRow(placement) ? FV_PLACEHOLDER : `${e}$`,
      width: COLUMNS_SIZE_MAP["16"],
    },
    {
      dataIndex: "orders",
      render: (e, placement: IPlacementDataTable) => (isFvRow(placement) ? FV_PLACEHOLDER : e),
      width: COLUMNS_SIZE_MAP["17"],
    },
    {
      dataIndex: "units",
      render: (e, placement: IPlacementDataTable) => (isFvRow(placement) ? FV_PLACEHOLDER : e),
      width: COLUMNS_SIZE_MAP["18"],
    },
    {
      dataIndex: "cvr",
      render: (e, placement: IPlacementDataTable) =>
        isFvRow(placement) ? FV_PLACEHOLDER : `${e}%`,
      width: COLUMNS_SIZE_MAP["19"],
    },
    {
      render: () => `-`,
      width: COLUMNS_SIZE_MAP["20"],
    },
    {
      render: () => `-`,
      width: COLUMNS_SIZE_MAP["21"],
    },
    {
      dataIndex: "acos",
      render: (e, placement: IPlacementDataTable) =>
        isFvRow(placement) ? FV_PLACEHOLDER : `${e}%`,
      width: COLUMNS_SIZE_MAP["22"],
    },
    {
      dataIndex: "cpc",
      render: (e, placement: IPlacementDataTable) =>
        isFvRow(placement) ? FV_PLACEHOLDER : `${e}$`,
      width: COLUMNS_SIZE_MAP["23"],
    },
    {
      dataIndex: "roas",
      render: (e, placement: IPlacementDataTable) => (isFvRow(placement) ? FV_PLACEHOLDER : e),
      width: COLUMNS_SIZE_MAP["24"],
    },
  ]
}

export default usePlacementColumns
